import { createContext, useContext, useEffect, useRef, type ReactNode } from "react";
import { Link } from "react-router";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { pageTitle } from "../lib/brand";

// The first page view keeps focus where the browser put it; after that, every
// route change moves focus to the new H1 so screen readers announce the page.
// Never out of a field the person is typing in: a page that finishes loading
// (its PageHeader mounts again) would otherwise swallow their Enter. Nor out of
// an open menu (the header's organisation or account menu), which would close it.
let firstPageView = true;

/**
 * No breadcrumbs inside the app's sidebar frame (Marius's rule N1; Edgar, 5 Nov 2026): the sidebar
 * shows the section and the tab you are in, and its tab leads back to the list. The other frames
 * (the government area, the portal, the contract manager) keep them.
 */
export const BreadcrumbsOff = createContext(false);
const FIELD_IN_USE = "input, textarea, select, [contenteditable]:not([contenteditable='false']), [aria-expanded='true'], [role='menu'] *";

/**
 * The design system's PageHeader: breadcrumbs above the H1, an optional lead line and
 * actions (top right).
 * Sets document.title to "{title} · Convenor" (the product name from /api/meta;
 * `documentTitle` overrides the first part). Use exactly one per page.
 */
export function PageHeader({
  title,
  lead,
  actions,
  breadcrumbs = [],
  meta,
  documentTitle,
  kicker,
}: {
  title: string;
  documentTitle?: string;
  /** The small uppercase label above the title. */
  kicker?: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: Crumb[];
  meta?: ReactNode;
}) {
  const h1 = useRef<HTMLHeadingElement>(null);
  const crumbsOff = useContext(BreadcrumbsOff);

  useEffect(() => {
    document.title = pageTitle(documentTitle ?? title);
  }, [title, documentTitle]);

  useEffect(() => {
    if (firstPageView) {
      firstPageView = false;
      return;
    }
    if (document.activeElement?.matches(FIELD_IN_USE)) return;
    h1.current?.focus();
  }, []);

  return (
    <div className="page-header">
      <div>
        {crumbsOff ? <BackLink crumbs={breadcrumbs} /> : <Breadcrumbs items={breadcrumbs} />}
        {kicker ? <span className="label">{kicker}</span> : null}
        <h1 ref={h1} tabIndex={-1}>
          {title}
        </h1>
        {meta ? <div className="page-header__meta">{meta}</div> : null}
        {lead ? <p className="lead">{lead}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

/**
 * Instead of the breadcrumb, in the sidebar frame: one link back to the page above, on a page that
 * sits below a record or a tab (a ToR's version, a mission's report, a ToR under Approvals).
 * Not on a section's or a tab's own page, nor on a record straight under its section (an expert
 * under Experts): the sidebar leads there. The crumbs end with the page itself.
 */
function BackLink({ crumbs }: { crumbs: Crumb[] }) {
  const parent = crumbs.length >= 4 ? crumbs[crumbs.length - 2] : undefined;
  if (!parent?.to) return null;
  return (
    <nav aria-label="Back">
      <div className="crumbs">
        <Link to={parent.to}>‹ Back to {parent.label}</Link>
      </div>
    </nav>
  );
}
